/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router';

import AddIcon from '@mui/icons-material/Add';
import DocumentationIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import { CircularProgress, IconButton, List, ListItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';

import scalefieldLogo from '../../assets/images/scalefield_logo.svg';
import { BasicRoutes, StructuralPageTypes } from '../../constants/basicRoutes';
import { LINKS } from '../../constants/settings';
import { Namespaces } from '../../i18n';
import { RootState } from '../../store';
import { fetchOrganization } from '../../store/organization/actions';
import { EntityId } from '../../types/api/EntityId';
import { Organization } from '../../types/organization/Organization';
import { isDataNotAsked } from '../../utils/dataState';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchProjectsWithClusters, fetchSetupProgress } from '../../utils/queryFns';
import { useControlOverlays } from '../../utils/useControlOverlays';
import { useOrganizationPermissions } from '../../utils/useOrganizationPermissions';
import { ClusterTreeItem } from '../ClusterTreeItem/ClusterTreeItem';
import { EntitiesList } from '../EntitiesList/EntitiesList';
import { EntitiesListItem } from '../EntitiesList/EntitiesListItem';
import { EntityThumbnail } from '../EntityThumbnail/EntityThumbnail';
import { NavigationDrawer, NavigationDrawerProps } from '../NavigationDrawer/NavigationDrawer';
import { ProjectTreeItem } from '../ProjectTreeItem/ProjectTreeItem';
import { SupportIcon } from '../svg-icons/SupportIcon';
import { WarningIcon } from '../svg-icons/WarningIcon';
import { BackOrgButton } from './components/BackOrgButton/BackOrgButton';
import { createNavigationStyles } from './Navigation.styles';

const mapState = (state: RootState) => ({
  organizations: state.organizations,
  auth: state.auth,
});

type PathParamsType = {
  organization_id: string;
  project_id: string;
  cluster_id: string;
};

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type NavigationProps = PropsFromRedux & NavigationDrawerProps;

const getProjectNodeId = (id: EntityId) => `project:${id}`;
const getClusterNodeId = (id: EntityId) => `cluster:${id}`;

const NavigationComponent = memo<NavigationProps>(props => {
  const { t } = useTranslation(Namespaces.Navigation);

  const { organizations, drawerWidth, drawerOpen, auth } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<PathParamsType>();

  const classes = createNavigationStyles({ drawerWidth });
  const regexp = new RegExp(`/${StructuralPageTypes.organization}/`);
  const isOrganizationRoute = regexp.test(location.pathname);
  const organizationIsFetching = false;
  const organizationId = params.organization_id!;
  const projectId = params.project_id!;
  const clusterId = params.cluster_id!;
  const isSuperuser = auth.user?.isSuperuser;
  const permissions = useOrganizationPermissions(organizationId);
  const { doOpenCreateOrganizationOverlay, doOpenCreateProjectOverlay } = useControlOverlays();

  const handleOrganizationClick = (org: Organization) => {
    void navigate(
      prepareRoutePath(BasicRoutes.ORGANIZATION_PAGE, {
        ':organization_id': org.id,
      }),
    );
  };

  const handleBackOrgButtonClick = () => {
    void navigate('/');
  };

  const onProjectClick = (id: EntityId) => {
    if (projectId !== id || clusterId) {
      void navigate(
        prepareRoutePath(BasicRoutes.PROJECT_PAGE, {
          ':organization_id': organizationId,
          ':project_id': id,
        }),
      );
    }
  };

  const onClusterClick = (id: EntityId, projectId: EntityId) => {
    if (clusterId !== id) {
      void navigate(
        prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
          ':organization_id': organizationId,
          ':project_id': projectId,
          ':cluster_id': id,
        }),
      );
    }
  };

  const [currentOrg, setCurrentOrg] = useState<undefined | Organization>(undefined);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');

  const isOrganizationSelected = !!currentOrg;

  const organizationProjectsQuery = useQuery(
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    [fetchProjectsWithClusters.key, { organizationId: currentOrg?.id! }],
    fetchProjectsWithClusters.fn,
    { enabled: isOrganizationSelected },
  );

  const organizationProjects = useMemo(
    () => organizationProjectsQuery.data?.data.payload || [],
    [organizationProjectsQuery.data],
  );

  const projectsError = useMemo(() => organizationProjectsQuery.status === 'error', [organizationProjectsQuery.status]);
  const projectsLoading = useMemo(
    () => organizationProjectsQuery.status === 'loading',
    [organizationProjectsQuery.status],
  );

  const setupProgressQuery = useQuery([fetchSetupProgress.key], fetchSetupProgress.fn);
  const setupProgress = useMemo(() => setupProgressQuery.data?.data.payload, [setupProgressQuery.data]);

  const isSetupCompleted = useMemo(() => {
    if (setupProgress) {
      return (
        setupProgress.steps.superuser === 'completed' &&
        setupProgress.steps.organization === 'completed' &&
        setupProgress.steps.emailConfig === 'completed'
      );
    }
    return true;
  }, [setupProgress]);

  useEffect(() => {
    void setupProgressQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations]);

  useEffect(() => {
    if (isOrganizationRoute) {
      const currentOrg = organizations.options.find(o => o.id === organizationId);
      setCurrentOrg(currentOrg);
    }
    if (clusterId) {
      setSelected(getClusterNodeId(clusterId));
    }
    return () => {
      setCurrentOrg(undefined);
      setSelected('');
    };
  }, [isOrganizationRoute, organizationId, organizations.options, clusterId]);

  useEffect(() => {
    if (projectId || clusterId) {
      setExpanded([getProjectNodeId(projectId)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganization(organizationId));
    }
  }, [organizationId, dispatch]);

  const handleExpandedItemsChange = (_e: React.SyntheticEvent, itemIds: string[]) => {
    setExpanded(itemIds);
  };

  return (
    <NavigationDrawer drawerWidth={drawerWidth} drawerOpen={drawerOpen}>
      <div className={classes.header}>
        <Link to="/">
          <img src={scalefieldLogo} alt="Logo" className={classes.imageLogo} data-cy="scalefield-homepage-logo" />
        </Link>
      </div>
      <div className={classes.content}>
        {isOrganizationSelected && isOrganizationRoute ? (
          <>
            {organizationIsFetching ? (
              <div className={classes.alignCenter}>
                <CircularProgress color="primary" size={40} />
              </div>
            ) : (
              <BackOrgButton onClick={handleBackOrgButtonClick} name={currentOrg?.name || ''} />
            )}
            <div className={classes.projectsTree}>
              <p className={classes.groupName}>
                {t('profile.projects')}
                <Tooltip title={!permissions.create_project ? t(`${Namespaces.Entities}:project.not_allowed`) : ''}>
                  <span>
                    <IconButton
                      aria-label="create project"
                      onClick={doOpenCreateProjectOverlay}
                      data-cy="navigation-add-project-button"
                      size="large"
                      disabled={!permissions.create_project}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </p>
              {projectsLoading ? (
                <div className={classes.alignCenter}>
                  <CircularProgress color="primary" size={40} />
                </div>
              ) : organizationProjects.length === 0 && projectsError ? (
                <div className={classes.noticeText}>{t('navigation.no_projects')}</div>
              ) : (
                <SimpleTreeView
                  expandedItems={expanded}
                  onExpandedItemsChange={handleExpandedItemsChange}
                  selectedItems={selected}
                >
                  {organizationProjects.length > 0 ? (
                    organizationProjects.map(project => (
                      <ProjectTreeItem
                        key={project.id}
                        itemId={getProjectNodeId(project.id)}
                        labelText={project.name}
                        labelIcon={
                          <EntityThumbnail
                            size={24}
                            type="project"
                            organizationId={organizationId}
                            projectId={project.id}
                            allowUploadImage={false}
                          />
                        }
                        onClick={() => onProjectClick(project.id)}
                      >
                        {project.clusters.length > 0 ? (
                          project.clusters.map(cluster => (
                            <ClusterTreeItem
                              key={[project.id, cluster.id].join('_')}
                              itemId={getClusterNodeId(cluster.id)}
                              label={cluster.name}
                              onClick={() => onClusterClick(cluster.id, project.id)}
                            />
                          ))
                        ) : (
                          <div className={classes.noticeText}>{t('navigation.no_clusters_in_project')}</div>
                        )}
                      </ProjectTreeItem>
                    ))
                  ) : (
                    <div className={classes.noticeText}>{t('navigation.no_projects_in_organization')}</div>
                  )}
                </SimpleTreeView>
              )}
            </div>
          </>
        ) : (
          <div className={classes.organizations}>
            <p className={classes.groupName}>
              {t('profile.organizations')}
              <Tooltip title={!isSuperuser ? t(`${Namespaces.Common}:home.no_organizations_annotation`) : ''}>
                <span>
                  <IconButton
                    disabled={!isSuperuser}
                    aria-label="create organization"
                    onClick={doOpenCreateOrganizationOverlay}
                    size="large"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </p>
            <EntitiesList
              isLoading={isDataNotAsked(organizations.dataState)}
              isEmpty={organizations.options.length === 0}
              noItemsTitle={<div className={classes.noticeText}>{t('navigation.no_organizations')}</div>}
              numberOfSkeletons={3}
            >
              {organizations.options.map(org => (
                <EntitiesListItem
                  key={org.id}
                  text={org.name}
                  onClick={() => handleOrganizationClick(org)}
                  icon={
                    <EntityThumbnail size={32} type="organization" organizationId={org.id} allowUploadImage={false} />
                  }
                />
              ))}
            </EntitiesList>
          </div>
        )}
      </div>

      <List className={classes.bottomLinks}>
        {isSuperuser && !isSetupCompleted && (
          <NavLink className={classes.navLink} to={BasicRoutes.SETUP} data-cy="navigation-list-setup-wizard-link">
            <ListItem button className={classes.listItem}>
              <ListItemIcon>
                <WarningIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.setup')} />
            </ListItem>
          </NavLink>
        )}
        <a
          className={classes.navLink}
          href={LINKS.DOCUMENTATION}
          target="_blank"
          data-cy="navigation-list-documentation-link"
        >
          <ListItem button className={classes.listItem}>
            <ListItemIcon>
              <DocumentationIcon />
            </ListItemIcon>
            <ListItemText primary={t('navigation.documentation')} />
          </ListItem>
        </a>
        <NavLink className={classes.navLink} to={'/support'}>
          <ListItem button className={classes.listItem}>
            <ListItemIcon>
              <SupportIcon />
            </ListItemIcon>
            <ListItemText primary={t('navigation.support')} />
          </ListItem>
        </NavLink>
        {isSuperuser && (
          <NavLink className={classes.navLink} to={BasicRoutes.ADMIN_HOME} data-cy="navigation-list-admin-section-link">
            <ListItem button className={classes.listItem}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.admin')} />
            </ListItem>
          </NavLink>
        )}
      </List>
    </NavigationDrawer>
  );
});

export const Navigation = connector(NavigationComponent);
